import { isChatContentWithinRetention } from '@/constants/chatRetention';
import { isBoundChatHistoryHidden } from '@/services/appPinSession';

const DB_NAME = 'duvpost_local_chat_v1';
const STORE = 'messages';
const DB_VERSION = 1;

export type LocalOnlyMessageRow = {
  id: string;
  threadId: string;
  cipherTextB64: string;
  ivB64: string;
  fromInviteToken: string;
  senderUid: string;
  createdAtMs: number;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        os.createIndex('byThread', 'threadId', { unique: false });
      }
    };
  });
}

export async function clearAllLocalOnlyMessages(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('indexedDB clear failed'));
    tx.objectStore(STORE).clear();
  });
}

export async function appendLocalOnlyMessage(row: LocalOnlyMessageRow): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('indexedDB write failed'));
    tx.objectStore(STORE).put(row);
  });
}

export async function deleteLocalOnlyMessage(messageId: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('indexedDB delete failed'));
    tx.objectStore(STORE).delete(messageId);
  });
}

export async function updateLocalOnlyMessageCipher(
  messageId: string,
  cipherTextB64: string,
  ivB64: string
): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('indexedDB update failed'));
    const store = tx.objectStore(STORE);
    const req = store.get(messageId);
    req.onerror = () => reject(req.error ?? new Error('indexedDB get failed'));
    req.onsuccess = () => {
      const row = req.result as LocalOnlyMessageRow | undefined;
      if (!row) {
        reject(new Error('local message not found'));
        return;
      }
      store.put({ ...row, cipherTextB64, ivB64 });
    };
  });
}

export async function listLocalOnlyMessages(threadId: string): Promise<LocalOnlyMessageRow[]> {
  if (isBoundChatHistoryHidden()) return [];
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const idx = store.index('byThread');
    const out: LocalOnlyMessageRow[] = [];
    const cur = idx.openCursor(IDBKeyRange.only(threadId));
    cur.onerror = () => reject(cur.error ?? new Error('indexedDB cursor failed'));
    cur.onsuccess = () => {
      const c = cur.result;
      if (c) {
        out.push(c.value as LocalOnlyMessageRow);
        c.continue();
      } else {
        out.sort((a, b) => a.createdAtMs - b.createdAtMs);
        const kept: LocalOnlyMessageRow[] = [];
        const expiredIds: string[] = [];
        for (const row of out) {
          if (isChatContentWithinRetention(row.createdAtMs)) kept.push(row);
          else expiredIds.push(row.id);
        }
        if (expiredIds.length === 0) {
          resolve(kept);
          return;
        }
        void (async () => {
          try {
            await Promise.all(expiredIds.map((id) => deleteLocalOnlyMessage(id)));
          } catch {
            /* best-effort local retention */
          }
          resolve(kept);
        })();
      }
    };
  });
}
